export interface Metrics {
  lines?: number;
  characters?: number;
  files?: number;
  generated_at?: string;
}

export interface FeedbackItem {
  text: string;
  user: string;
}

export interface AdvertiserSnapshot {
  positives: number | null;
  visits: number;
  ctr: number;
  conversion: number;
  usingFallback: boolean;
}

export interface ShopCategory {
  id: string;
  title: string;
  copy: string;
  href: string;
  cta?: string;
  icon?: string;
}

export interface FeaturedProduct {
  id: string;
  title: string;
  price: string;
  priceCents?: number | null;
  vendor: 'BrickLink' | 'eBay' | 'weretrade' | 'Amazon';
  href: string;
  badge?: string;
  image?: string;
}

export interface UnifiedMetrics {
  meta?: { generated_at?: string; };
  apps?: { total: number; internal: number; external: number; };
  workers?: { total: number; };
  code?: { total_lines: number; total_files: number; };
  features?: { total_shipped: number; };
  commits?: { total: number; period?: string; };
  /**
   * `bugs_source` is part of the contract on purpose: the generator reports
   * "none" when it found nothing to count, and a 0 that means "not measured"
   * must never render as a 0 that means "no bugs left".
   */
  quality?: { bugs_fixed: number; bugs_source?: string; };
  timeline?: { start_date?: string; months_elapsed: number; };
}

export interface LandingLoaderData {
  metrics: Metrics | null;
  unifiedMetrics: UnifiedMetrics | null;
  feedback: {
    total: number | null;
    quotes: FeedbackItem[];
    status: string;
  };
  advertiserStats: AdvertiserSnapshot;
  shop: {
    categories: ShopCategory[];
    featured: FeaturedProduct[];
  };
}

const INVESTOR_PROGRESS_BASE_URL = 'https://investor-progress.weretradeit.workers.dev';
const SAME_ORIGIN_SHOP_CONFIG_URL = '/api/shop/public-config';
const SAME_ORIGIN_INVENTAR_FEED_URL = '/api/inventar/shop-feed';

const getEnv = () => {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const aliasMatch = hostname.match(/^([^.]+)\.[^.]+\.pages\.dev$/);
  // @ts-ignore
  const alias = (aliasMatch && aliasMatch[1]) || window.PAGES_ALIAS_ENV || 'production';
  const envSuffix = alias.toLowerCase() !== 'production' ? `-${alias}` : '';
  // @ts-ignore
  const workersSub = (window.WORKERS_SUBDOMAIN || 'weretrade.workers.dev').trim();
  return { envSuffix, workersSub };
};

export async function landingLoader(): Promise<LandingLoaderData> {
  const { envSuffix, workersSub } = getEnv();

  const [metrics, feedback, advertiserStats, shop, unifiedMetrics] = await Promise.all([
    fetchMetrics(envSuffix, workersSub),
    fetchFeedback(envSuffix, workersSub),
    fetchMarketingSnapshot(envSuffix, workersSub),
    fetchShopConfig(),
    fetchUnifiedMetrics()
  ]);

  // Consolidate feedback totals if needed
  const consolidatedFeedback = { ...feedback };
  if (advertiserStats.positives != null) {
    consolidatedFeedback.total = (feedback.total == null || feedback.total < advertiserStats.positives)
      ? advertiserStats.positives
      : feedback.total;
  }

  return {
    metrics,
    feedback: consolidatedFeedback,
    advertiserStats,
    shop,
    unifiedMetrics
  };
}

async function fetchUnifiedMetrics(): Promise<UnifiedMetrics | null> {
  try {
    const dateKey = new Date().toISOString().split('T')[0];
    const res = await fetch(`${INVESTOR_PROGRESS_BASE_URL}/unified-metrics?v=${dateKey}`, { credentials: 'omit', cache: 'no-store' });
    if (res.ok) return await res.json();
  } catch (_) { /* Actively discarded */ }
  return null;
}

async function fetchMetrics(envSuffix: string, workersSub: string): Promise<Metrics | null> {
  const workerHost = (name: string) => `https://${name}${envSuffix}.${workersSub}`;
  const servicesHost = envSuffix && envSuffix !== ''
    ? `https://services-${envSuffix}.weretrade.com/linesOfCode`
    : 'https://services.weretrade.com/linesOfCode';

  const sources = [
    `https://code-metrics-production.${workersSub}`,
    workerHost('code-metrics'),
    `https://code-metrics${envSuffix}.${workersSub}`,
    `https://services-design.pages.dev/linesOfCode`,
    servicesHost,
  ];

  for (const url of sources) {
    try {
      const res = await fetch(url, { credentials: 'omit' });
      if (res.ok) return await res.json();
    } catch (_) { /* Actively discarded: Handled harmless exception at boundary */ }
  }
  return null;
}

async function fetchFeedback(envSuffix: string, workersSub: string) {
  const sources = [
    `https://bricklink-store-metrics-production.${workersSub}/feedback`,
    `https://ebay-store-metrics-production.${workersSub}/feedback`,
    `https://bricklink-store-metrics${envSuffix}.${workersSub}/feedback`,
    `https://ebay-store-metrics${envSuffix}.${workersSub}/feedback`,
  ];

  let total = 0;
  let pulled: FeedbackItem[] = [];
  let ok = false;

  for (const url of sources) {
    try {
      const res = await fetch(url, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const feedbackRaw = (data?.feedback || data?.items || data?.latestFeedbacks || []);
        const feedback = (Array.isArray(feedbackRaw) ? feedbackRaw : []).filter(
          (f: any) => !f.rating || String(f.rating).toLowerCase() === 'positive'
        );
        const count = data?.totalPositive || data?.total || data?.count || data?.feedbackCount || feedback.length || 0;
        total += count;
        pulled = pulled.concat(
          feedback.slice(0, 6).map((f: any) => ({
            text: (f.comment || f.text || f.feedback || '').toString().trim(),
            user: (f.user || f.author || '').toString().trim() || 'Anonym',
          }))
        );
        ok = true;
      }
    } catch (_) { /* Actively discarded: Handled harmless exception at boundary */ }
  }

  const fallback: FeedbackItem[] = [
    { text: 'Schneller Versand, alles wie beschrieben.', user: 'BrickLink Käufer' },
    { text: 'Top Service, gerne wieder!', user: 'eBay Käufer' },
    { text: 'Zuverlässig und gut verpackt.', user: 'Stammkunde' },
  ];

  const quotes = (pulled.length > 0 ? pulled.filter((q) => q.text) : fallback)
    .sort((a, b) => b.text.length - a.text.length)
    .slice(0, 6);

  return {
    total: total || null,
    quotes,
    status: ok ? 'Feedback geladen' : 'Feedback aktuell nicht erreichbar (zeige Beispiele)'
  };
}

async function fetchMarketingSnapshot(envSuffix: string, workersSub: string): Promise<AdvertiserSnapshot> {
  const envAwareServices = envSuffix && envSuffix !== ''
    ? `https://services-${envSuffix}.weretrade.com/marketingSnapshot`
    : 'https://services.weretrade.com/marketingSnapshot';

  const sources = [
    `https://code-metrics-production.${workersSub}/marketingSnapshot`,
    `https://code-metrics${envSuffix}.${workersSub}/marketingSnapshot`,
    'https://services-design.pages.dev/marketingSnapshot',
    envAwareServices,
  ];

  for (const url of sources) {
    try {
      const res = await fetch(url, { credentials: 'omit', cache: 'no-store' });
      if (res.ok) {
        const data = await res.json();
        return {
          positives: typeof data.positives === 'number' ? data.positives : null,
          visits: typeof data.visits === 'number' ? data.visits : 42000,
          ctr: typeof data.ctr === 'number' ? data.ctr : 3.4,
          conversion: typeof data.conversion === 'number' ? data.conversion : 2.2,
          usingFallback: false,
        };
      }
    } catch (_) { /* Actively discarded: Handled harmless exception at boundary */ }
  }

  return {
    positives: null,
    visits: 42000,
    ctr: 3.4,
    conversion: 2.2,
    usingFallback: true,
  };
}

async function fetchShopConfig() {
  const hosts = [
    SAME_ORIGIN_SHOP_CONFIG_URL,
    SAME_ORIGIN_INVENTAR_FEED_URL,
  ];

  for (const url of hosts) {
    try {
      const res = await fetch(url, { credentials: 'omit', cache: 'no-store' });
      if (res.ok) {
        const data = await res.json();
        if (data?.ok && data?.featured) {
          return {
            featured: data.featured,
            categories: (data.categories || []).map((c: any) => ({
              id: c.slug || c.id,
              title: c.name,
              copy: c.description || 'Entdecken Sie unsere Produkte',
              href: '#cart',
              cta: 'Ansehen',
              icon: c.icon_emoji || '📦',
            }))
          };
        }
        if (Array.isArray(data)) {
          const featured = mapFeedToFeatured(data);
          const cats = Array.from(
            new Set(data.map((d: any) => d.category).filter((c: any) => Boolean(c)))
          ).slice(0, 6);

          return {
            featured,
            categories: cats.map((c: any) => ({
              id: c.toLowerCase().replace(/\s+/g, '-'),
              title: c,
              copy: 'Auswahl aus Inventar Owl',
              href: '#cart',
              cta: 'Ansehen',
            }))
          };
        }
      }
    } catch (_) { /* Actively discarded: Handled harmless exception at boundary */ }
  }

  return { categories: [], featured: [] };
}

function mapFeedToFeatured(items: any[]): FeaturedProduct[] {
  return items.map((it) => ({
    id: it.boid || it.id || (Math.random().toString(36).substring(7)),
    title: it.title || it.name || 'Artikel',
    price: typeof it.price_cents === 'number' ? `€${(it.price_cents / 100).toFixed(2)}` : it.price || '—',
    priceCents: typeof it.price_cents === 'number' ? it.price_cents : it.priceCents ?? null,
    vendor: it.vendor || 'weretrade',
    href: it.href || it.url || '#',
    badge: it.badge || (it.featured ? 'Featured' : undefined),
    image: it.hero_url || it.image || undefined,
  }));
}
